"use client";

import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from "react";
import createId from "@/lib/create-id";
import type { CarePlanCycleDetail } from "@/types/care-plan";
import { getMessageTemplateOffsetMinutes } from "./get-message-template-offset";
import { type CarePlanBranch, type CarePlanStep, CarePlanStepStatus, type DragOverMoveInfo, type DragOverMoveParams, type DuplicatedCycleInfo, PatientCarePlanStep, StepType, buildBranchIdMap, findSelfContainedCycles, findStep, findStepInsertIndex, isLinkedMessage, isPatientStep, locateCycleInStep, moveStepBetweenContainers, reconcileCycleScope, remapCycleIds, removeCyclesAndReparent, removeStepFromTree, resolveDragOverMove, updateBranchInTree, updateStepInTree } from "./types";
const DEFAULT_OFFSET_MINUTES = 1440; // 1 day

/**
 * End of a timeline step for chaining the next step: decision steps advance by
 * shortest branch duration (matches recalculatePositions / enrichBranchDates).
 */
function getEffectiveTimelineEndDate(step: PatientCarePlanStep): Date {
  const stepDate = new Date(step.scheduledDate);
  if (step.type === StepType.DECISION && (step.branches?.length ?? 0) > 0) {
    const shortestDuration = getShortestBranchDurationMinutes(step);
    return new Date(stepDate.getTime() + shortestDuration * 60000);
  }
  return stepDate;
}

/**
 * After timeline dates change at the top level, re-derive message scheduledDate
 * from each parent (same placement rules as template instantiation).
 */
function resyncTopLevelMessageDates(steps: PatientCarePlanStep[]): PatientCarePlanStep[] {
  const parentById = new Map<string, PatientCarePlanStep>();
  for (const s of steps) {
    if (s.type !== StepType.MESSAGE) {
      parentById.set(s.id, s);
    }
  }
  return steps.map(step => {
    if (step.type !== StepType.MESSAGE || !step.parentStepId) {
      return step;
    }
    const parent = parentById.get(step.parentStepId);
    if (!parent) return step;
    const parentDate = new Date(parent.scheduledDate).getTime();
    const offsetMin = getMessageTemplateOffsetMinutes(step);
    return {
      ...step,
      scheduledDate: new Date(parentDate + offsetMin * 60000).toISOString()
    };
  });
}

/**
 * From the given timeline index onward, set each step's scheduledDate from the
 * previous step's effective end + minutesAfterPrevious. Preserves plan order.
 * Index 0 uses the step's current scheduledDate (e.g. user edit).
 */
function cascadeTopLevelTimelineDatesFromIndex(steps: PatientCarePlanStep[], startTimelineIndex: number): PatientCarePlanStep[] {
  let timelineIdx = -1;
  let prevEffectiveEnd: Date | null = null;
  return steps.map(step => {
    if (isLinkedMessage(step)) {
      return step;
    }
    timelineIdx += 1;
    if (timelineIdx < startTimelineIndex) {
      prevEffectiveEnd = getEffectiveTimelineEndDate(step);
      return step;
    }
    if (timelineIdx === startTimelineIndex) {
      let next = {
        ...step
      } as PatientCarePlanStep;
      const stepDate = new Date(next.scheduledDate);
      if (next.type === StepType.DECISION && next.branches) {
        next = {
          ...next,
          branches: next.branches.map(branch => ({
            ...branch,
            steps: recalculateBranchPositions(branch.steps as PatientCarePlanStep[], stepDate)
          }))
        };
      }
      prevEffectiveEnd = getEffectiveTimelineEndDate(next);
      return next;
    }
    const offsetMs = (step.minutesAfterPrevious ?? DEFAULT_OFFSET_MINUTES) * 60000;
    const newDate = new Date(prevEffectiveEnd!.getTime() + offsetMs);
    let updated: PatientCarePlanStep = {
      ...step,
      scheduledDate: newDate.toISOString(),
      isDateEstimated: true
    };
    if (updated.type === StepType.DECISION && updated.branches) {
      updated = {
        ...updated,
        branches: updated.branches.map(branch => ({
          ...branch,
          steps: recalculateBranchPositions(branch.steps as PatientCarePlanStep[], newDate)
        }))
      };
    }
    prevEffectiveEnd = getEffectiveTimelineEndDate(updated);
    return updated;
  });
}

/**
 * Recursively deep-clone a step, assigning fresh IDs to the step itself
 * and all nested branches / sub-steps. Updates parentStepId and
 * decisionStepId references so the cloned subtree is fully independent.
 */
function deepCloneStep<T extends CarePlanStep>(step: T, newStepId?: string): T {
  const id = newStepId ?? createId("cps");
  const cloned = {
    ...step,
    id,
    measurements: step.measurements.map(m => ({
      ...m,
      id: undefined
    }))
  } as T;
  if (step.type === StepType.DECISION && step.branches) {
    cloned.branches = step.branches.map(branch => {
      const newBranchId = createId("cpb");
      return {
        ...branch,
        id: newBranchId,
        decisionStepId: id,
        selectedDate: undefined,
        selectedById: undefined,
        steps: branch.steps.map(subStep => {
          const clonedSub = deepCloneStep(subStep);
          if (clonedSub.branchId) {
            return {
              ...clonedSub,
              branchId: newBranchId
            };
          }
          return clonedSub;
        })
      };
    });

    // Fix parentStepId references within each cloned branch.
    for (let bIdx = 0; bIdx < (step.branches ?? []).length; bIdx++) {
      const origBranch = step.branches![bIdx];
      const clonedBranch = cloned.branches![bIdx];
      const idMap = new Map<string, string>();
      for (let sIdx = 0; sIdx < origBranch.steps.length; sIdx++) {
        idMap.set(origBranch.steps[sIdx].id, clonedBranch.steps[sIdx].id);
      }
      clonedBranch.steps = clonedBranch.steps.map(s => {
        if (s.parentStepId && idMap.has(s.parentStepId)) {
          return {
            ...s,
            parentStepId: idMap.get(s.parentStepId)!
          };
        }
        return s;
      });
    }
  }

  // Reset patient-specific linked fields on the cloned step
  if (isPatientStep(cloned)) {
    (cloned as PatientCarePlanStep).status = CarePlanStepStatus.PENDING;
    (cloned as PatientCarePlanStep).eventId = undefined;
    (cloned as PatientCarePlanStep).taskId = undefined;
  }
  return cloned;
}
function createDefaultBranches(decisionStepId: string): CarePlanBranch[] {
  return [{
    id: createId("cpb"),
    decisionStepId,
    position: 0,
    name: "Option A",
    tagIds: [],
    steps: []
  }, {
    id: createId("cpb"),
    decisionStepId,
    position: 1,
    name: "Option B",
    tagIds: [],
    steps: []
  }];
}
function createDefaultStep(stepNumber: number, type: StepType = StepType.APPOINTMENT): PatientCarePlanStep {
  const id = createId("cps");
  return {
    kind: "patient",
    id,
    stepNumber,
    type,
    serviceIds: [],
    medicationIds: [],
    measurements: [],
    minutesAfterPrevious: 0,
    scheduledDate: new Date().toISOString(),
    status: CarePlanStepStatus.PENDING,
    ...(type === StepType.DECISION && {
      branches: createDefaultBranches(id)
    })
  };
}

/**
 * Get the total duration in minutes of a branch, measured as the sum
 * of minutesAfterPrevious across all timeline steps in the branch.
 */
export function getBranchDurationMinutes(branch: CarePlanBranch): number {
  return branch.steps.filter(s => !isLinkedMessage(s)).reduce((total, s) => total + (s.minutesAfterPrevious ?? 0), 0);
}

/**
 * Get the shortest branch duration in minutes across all branches
 * of a decision step. Returns 0 if there are no branches or all are empty.
 */
export function getShortestBranchDurationMinutes(step: CarePlanStep): number {
  const branches = step.branches ?? [];
  if (branches.length === 0) return 0;
  const durations = branches.map(getBranchDurationMinutes);
  return Math.min(...durations);
}

/**
 * Recalculate positions and estimated dates for steps inside a branch.
 * Dates are computed forward from the decision step's date using each
 * step's minutesAfterPrevious offset.
 */
function recalculateBranchPositions(steps: CarePlanStep[], decisionDate: Date): CarePlanStep[] {
  let prevTimelineDate = decisionDate;
  return steps.map((step, i) => {
    if (isLinkedMessage(step)) {
      return {
        ...step,
        stepNumber: i + 1
      };
    }
    const estimatedDate = new Date(prevTimelineDate.getTime() + (step.minutesAfterPrevious ?? 0) * 60000);
    const updated: CarePlanStep = isPatientStep(step) ? {
      ...step,
      stepNumber: i + 1,
      scheduledDate: estimatedDate.toISOString(),
      isDateEstimated: true
    } : {
      ...step,
      kind: "patient" as const,
      stepNumber: i + 1,
      scheduledDate: estimatedDate.toISOString(),
      isDateEstimated: true,
      status: CarePlanStepStatus.PENDING
    };
    if (step.type === StepType.DECISION && updated.branches) {
      updated.branches = updated.branches.map(branch => ({
        ...branch,
        steps: recalculateBranchPositions(branch.steps, estimatedDate)
      }));
      const shortestDuration = getShortestBranchDurationMinutes(updated);
      prevTimelineDate = new Date(estimatedDate.getTime() + shortestDuration * 60000);
    } else {
      prevTimelineDate = estimatedDate;
    }
    return updated;
  });
}

/**
 * Re-number steps and recalculate minutesAfterPrevious offsets from the
 * current scheduled dates. Also recalculates estimated dates for branch
 * steps and accounts for branch durations when computing offsets after
 * decision steps.
 */
function recalculatePositions(steps: PatientCarePlanStep[]): PatientCarePlanStep[] {
  let prevTimelineDate: Date | null = null;
  return steps.map((step, i) => {
    const updated = {
      ...step,
      stepNumber: i + 1
    };
    if (!isLinkedMessage(step)) {
      let stepDate = new Date(updated.scheduledDate);

      // If step falls before the effective end of a previous decision's
      // branches, push it forward so dates stay chronological.
      if (prevTimelineDate && stepDate.getTime() < prevTimelineDate.getTime()) {
        const offset = step.minutesAfterPrevious ?? DEFAULT_OFFSET_MINUTES;
        stepDate = new Date(prevTimelineDate.getTime() + offset * 60000);
        updated.scheduledDate = stepDate.toISOString();
        updated.isDateEstimated = true;
      }
      if (prevTimelineDate) {
        updated.minutesAfterPrevious = Math.max(0, Math.round((stepDate.getTime() - prevTimelineDate.getTime()) / 60000));
      }
      if (step.type === StepType.DECISION && updated.branches) {
        updated.branches = updated.branches.map(branch => ({
          ...branch,
          steps: recalculateBranchPositions(branch.steps as PatientCarePlanStep[], stepDate)
        }));
        const shortestDuration = getShortestBranchDurationMinutes(updated);
        prevTimelineDate = new Date(stepDate.getTime() + shortestDuration * 60000);
      } else {
        prevTimelineDate = stepDate;
      }
    }
    return updated;
  });
}

/**
 * Reorder steps while keeping scheduled dates pinned to their positions.
 * E.g. if dates are [Mar 1, Mar 5, Mar 7], after any reorder those dates
 * stay at positions [0, 1, 2] — only which step occupies each slot changes.
 */
function reorderWithPinnedDates(steps: PatientCarePlanStep[]): PatientCarePlanStep[] {
  // Collect the original timeline dates in positional order
  const timelineDates: string[] = [];
  for (const s of steps) {
    if (!isLinkedMessage(s)) {
      timelineDates.push(s.scheduledDate);
    }
  }
  // Sort dates chronologically so position 0 = earliest
  timelineDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  // Assign sorted dates back to timeline steps by position
  let timelineIdx = 0;
  let prevTimelineDate: Date | null = null;
  return steps.map((step, i) => {
    const updated = {
      ...step,
      stepNumber: i + 1
    };
    if (!isLinkedMessage(step)) {
      updated.scheduledDate = timelineDates[timelineIdx];
      let stepDate = new Date(updated.scheduledDate);
      if (prevTimelineDate && stepDate.getTime() < prevTimelineDate.getTime()) {
        const offset = step.minutesAfterPrevious ?? DEFAULT_OFFSET_MINUTES;
        stepDate = new Date(prevTimelineDate.getTime() + offset * 60000);
        updated.scheduledDate = stepDate.toISOString();
        updated.isDateEstimated = true;
      }
      if (prevTimelineDate) {
        updated.minutesAfterPrevious = Math.max(0, Math.round((stepDate.getTime() - prevTimelineDate.getTime()) / 60000));
      }
      if (step.type === StepType.DECISION && updated.branches) {
        updated.branches = updated.branches.map(branch => ({
          ...branch,
          steps: recalculateBranchPositions(branch.steps as PatientCarePlanStep[], stepDate)
        }));
        const shortestDuration = getShortestBranchDurationMinutes(updated);
        prevTimelineDate = new Date(stepDate.getTime() + shortestDuration * 60000);
      } else {
        prevTimelineDate = stepDate;
      }
      timelineIdx++;
    }
    return updated;
  });
}
function applyMoveResult(prev: PatientCarePlanStep[], params: DragOverMoveParams): PatientCarePlanStep[] {
  const result = moveStepBetweenContainers(prev, params);
  if (result.steps === prev) return prev;
  let next = result.steps as PatientCarePlanStep[];
  if (result.movedFromContainer === "root" || result.movedToContainer === "root") {
    next = reorderWithPinnedDates(next);
  }
  const branchesToRecalc = new Set<string>();
  for (const c of [result.movedFromContainer, result.movedToContainer]) {
    if (c && c !== "root") branchesToRecalc.add(c.slice("branch:".length));
  }
  for (const branchId of Array.from(branchesToRecalc)) {
    next = updateBranchInTree(next, branchId, (branch, decisionStep) => {
      const decisionDate = isPatientStep(decisionStep) ? new Date(decisionStep.scheduledDate) : new Date();
      return {
        ...branch,
        steps: recalculateBranchPositions(branch.steps, decisionDate)
      };
    });
  }
  return next;
}

/**
 * Derive estimated dates for all branch steps and push post-decision
 * steps forward when they fall before the shortest branch ends.
 * Applied as a read-only pass on every render so the UI always has dates.
 */
function enrichBranchDates(steps: PatientCarePlanStep[]): PatientCarePlanStep[] {
  let prevTimelineDate: Date | null = null;
  return steps.map(step => {
    if (isLinkedMessage(step)) return step;
    let stepDate = new Date(step.scheduledDate);

    // Push step forward if it falls before the end of a previous decision's branches
    if (prevTimelineDate && stepDate.getTime() < prevTimelineDate.getTime()) {
      const offset = step.minutesAfterPrevious ?? DEFAULT_OFFSET_MINUTES;
      stepDate = new Date(prevTimelineDate.getTime() + offset * 60000);
      step = {
        ...step,
        scheduledDate: stepDate.toISOString(),
        isDateEstimated: true
      };
    }
    if (step.type === StepType.DECISION && step.branches) {
      const enriched = {
        ...step,
        branches: step.branches.map(branch => ({
          ...branch,
          steps: recalculateBranchPositions(branch.steps, stepDate)
        }))
      };
      const shortestDuration = getShortestBranchDurationMinutes(enriched);
      prevTimelineDate = new Date(stepDate.getTime() + shortestDuration * 60000);
      return enriched;
    }
    prevTimelineDate = stepDate;
    return step;
  });
}
const NOOP_SET_STEPS: Dispatch<SetStateAction<PatientCarePlanStep[]>> = () => {};
export function usePatientCarePlan(steps: PatientCarePlanStep[], setSteps?: Dispatch<SetStateAction<PatientCarePlanStep[]>>, startDate?: Date, options?: {
  cycles?: CarePlanCycleDetail[];
  setCycles?: Dispatch<SetStateAction<CarePlanCycleDetail[]>>;
  onCyclesDuplicated?: (info: DuplicatedCycleInfo[]) => void;
}) {
  const stableSetSteps = setSteps ?? NOOP_SET_STEPS;
  const cyclesRef = options?.cycles;
  const setCyclesRef = options?.setCycles;
  function applyMoveAndReconcile(prev: PatientCarePlanStep[], move: DragOverMoveParams): {
    steps: PatientCarePlanStep[];
    removedCycleIds: string[];
  } {
    const moved = applyMoveResult(prev, move);
    if (moved === prev || !cyclesRef || cyclesRef.length === 0) {
      return {
        steps: moved,
        removedCycleIds: []
      };
    }
    const r = reconcileCycleScope(prev, moved, cyclesRef);
    return {
      steps: r.steps as PatientCarePlanStep[],
      removedCycleIds: r.removedCycleIds
    };
  }
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);

  // Enrich branch steps with estimated dates on every render pass
  const enrichedSteps = useMemo(() => enrichBranchDates(steps), [steps]);
  const selectedStep = selectedStepId ? findStep(enrichedSteps, selectedStepId) : null;
  const addStep = useCallback((type?: StepType) => {
    const newStep = createDefaultStep(0, type);
    stableSetSteps(prev => {
      // Compute scheduledDate from last timeline step
      const lastTimelineStep = [...prev].reverse().find(s => !isLinkedMessage(s));
      if (lastTimelineStep) {
        const lastDate = new Date(lastTimelineStep.scheduledDate);
        // If last step is a decision, offset from end of its shortest branch
        const branchOffset = lastTimelineStep.type === StepType.DECISION ? getShortestBranchDurationMinutes(lastTimelineStep) * 60 * 1000 : 0;
        const computedDate = new Date(lastDate.getTime() + branchOffset + DEFAULT_OFFSET_MINUTES * 60 * 1000);
        return [...prev, {
          ...newStep,
          stepNumber: prev.length + 1,
          scheduledDate: computedDate.toISOString(),
          minutesAfterPrevious: DEFAULT_OFFSET_MINUTES
        }];
      }
      return [...prev, {
        ...newStep,
        stepNumber: prev.length + 1,
        scheduledDate: (startDate ?? new Date()).toISOString()
      }];
    });
    setSelectedStepId(newStep.id);
  }, [stableSetSteps, startDate]);
  const duplicateStep = useCallback((step: CarePlanStep, atTimelineIndex: number) => {
    const patientStep = step as PatientCarePlanStep;
    const sourceDate = new Date(patientStep.scheduledDate).getTime();
    const newDate = new Date(sourceDate + (patientStep.minutesAfterPrevious ?? DEFAULT_OFFSET_MINUTES) * 60 * 1000).toISOString();

    // DFS: find all cycles self-contained within this step (including branch descendants)
    const selfContained = options?.cycles?.length ? findSelfContainedCycles(step, steps, options.cycles) : [];
    const cycleIdMap = new Map<string, string>();
    const pendingLocations: Array<{
      cycle: CarePlanCycleDetail;
      loc: ReturnType<typeof locateCycleInStep>;
    }> = [];
    for (const cycle of selfContained) {
      const newId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      cycleIdMap.set(cycle.id, newId);
      pendingLocations.push({
        cycle: {
          ...cycle,
          id: newId
        },
        loc: locateCycleInStep(step, cycle.id)
      });
    }
    // Remap parentCycleId so child cycles point to the new parent
    for (const entry of pendingLocations) {
      if (entry.cycle.parentCycleId && cycleIdMap.has(entry.cycle.parentCycleId)) {
        entry.cycle = {
          ...entry.cycle,
          parentCycleId: cycleIdMap.get(entry.cycle.parentCycleId)!
        };
      }
    }
    let newStep = deepCloneStep(patientStep);
    newStep.scheduledDate = newDate;
    if (cycleIdMap.size > 0) {
      newStep = remapCycleIds(newStep, cycleIdMap);
    }
    const branchIdMap = buildBranchIdMap(step, newStep);
    const duplicatedCycles: DuplicatedCycleInfo[] = [];
    for (const {
      cycle,
      loc
    } of pendingLocations) {
      if (!loc) continue;
      if (loc.branchId === null) {
        duplicatedCycles.push({
          cycle,
          fromStepId: newStep.id,
          toStepId: newStep.id,
          branchId: null
        });
      } else {
        // Resolve branch step IDs from the cloned step's branches
        const newBranchId = branchIdMap.get(loc.branchId) ?? loc.branchId;
        const branch = newStep.branches?.find(b => b.id === newBranchId);
        const timeline = branch ? branch.steps.filter(s => !isLinkedMessage(s)) : [];
        const fromId = timeline[loc.fromIndex]?.id;
        const toId = timeline[loc.toIndex]?.id;
        if (fromId && toId) {
          duplicatedCycles.push({
            cycle,
            fromStepId: fromId,
            toStepId: toId,
            branchId: newBranchId
          });
        }
      }
    }
    stableSetSteps(prev => {
      const children = prev.filter(s => isLinkedMessage(s) && s.parentStepId === step.id) as PatientCarePlanStep[];
      const newChildMessages: PatientCarePlanStep[] = children.map(c => {
        const offsetMs = new Date(c.scheduledDate).getTime() - sourceDate;
        return {
          ...c,
          id: createId("cps"),
          parentStepId: newStep.id,
          scheduledDate: new Date(new Date(newDate).getTime() + offsetMs).toISOString(),
          status: CarePlanStepStatus.PENDING,
          eventId: undefined
        };
      });
      const insertAt = findStepInsertIndex(prev, atTimelineIndex);
      const toInsert = [newStep, ...newChildMessages];
      const next = [...prev.slice(0, insertAt), ...toInsert, ...prev.slice(insertAt)];
      return recalculatePositions(next);
    });
    if (duplicatedCycles.length > 0) {
      options?.onCyclesDuplicated?.(duplicatedCycles);
    }
    setSelectedStepId(newStep.id);
  }, [stableSetSteps, steps, options?.cycles, options?.onCyclesDuplicated]);
  const updateStep = useCallback((id: string, updates: Partial<PatientCarePlanStep>) => {
    stableSetSteps(prev => {
      const timelineBefore = prev.filter(s => !isLinkedMessage(s));
      const editedTimelineIndex = timelineBefore.findIndex(s => s.id === id);
      const updated = updateStepInTree(prev, id, updates) as PatientCarePlanStep[];
      if (!("scheduledDate" in updates)) return updated;

      // First top-level timeline step: preserve sequence and move later steps
      // by their minutesAfterPrevious offsets (matches template instantiation).
      if (editedTimelineIndex === 0) {
        const cascaded = cascadeTopLevelTimelineDatesFromIndex(updated, 0);
        return recalculatePositions(resyncTopLevelMessageDates(cascaded));
      }

      // When a scheduledDate changes on a later step, re-sort by date so the
      // visual order matches chronological order.
      const timeline = updated.filter(s => !isLinkedMessage(s));
      timeline.sort((a, b) => {
        const dateDiff = new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime();
        if (dateDiff !== 0) return dateDiff;
        return (a.cyclePass ?? 0) - (b.cyclePass ?? 0);
      });

      // Rebuild full array: each timeline step followed by its child messages
      const childrenMap = new Map<string, PatientCarePlanStep[]>();
      for (const s of updated) {
        if (isLinkedMessage(s)) {
          const children = childrenMap.get(s.parentStepId) ?? [];
          children.push(s);
          childrenMap.set(s.parentStepId, children);
        }
      }
      const result: PatientCarePlanStep[] = [];
      for (const step of timeline) {
        result.push(step);
        const children = childrenMap.get(step.id);
        if (children) result.push(...children);
      }
      return recalculatePositions(result);
    });
  }, [stableSetSteps]);
  const removeStep = useCallback((id: string) => {
    stableSetSteps(prev => {
      const stepToRemove = findStep(prev, id);
      // Don't allow removing completed or linked steps
      if (stepToRemove && isPatientStep(stepToRemove) && (stepToRemove.status === CarePlanStepStatus.COMPLETED || stepToRemove.eventId || stepToRemove.taskId)) {
        return prev;
      }
      // Remove step from anywhere in the tree (top-level or branch)
      const updated = removeStepFromTree(prev, id);
      return updated === prev ? prev : recalculatePositions(updated);
    });
    setSelectedStepId(curr => curr === id ? null : curr);
    setExpandedStepId(curr => curr === id ? null : curr);
  }, [stableSetSteps]);
  const selectStep = useCallback((id: string | null) => {
    setSelectedStepId(id);
  }, []);
  const toggleStepExpanded = useCallback((id: string) => {
    setExpandedStepId(prev => prev === id ? null : id);
  }, []);

  /**
   * Move a timeline step (and its child messages) from one position to another.
   * Indices refer to positions in the filtered non-message step list.
   */
  const moveStep = useCallback((fromIndex: number, toIndex: number) => {
    stableSetSteps(prev => {
      const timeline = prev.filter(s => !isLinkedMessage(s));
      if (fromIndex < 0 || fromIndex >= timeline.length || toIndex < 0 || toIndex >= timeline.length || fromIndex === toIndex) {
        return prev;
      }

      // Reorder timeline steps
      const reordered = [...timeline];
      const [removed] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, removed);

      // Rebuild full array: each timeline step followed by its child messages
      const childrenMap = new Map<string, PatientCarePlanStep[]>();
      for (const s of prev) {
        if (isLinkedMessage(s)) {
          const children = childrenMap.get(s.parentStepId) ?? [];
          children.push(s);
          childrenMap.set(s.parentStepId, children);
        }
      }
      const result: PatientCarePlanStep[] = [];
      for (const step of reordered) {
        result.push(step);
        const children = childrenMap.get(step.id);
        if (children) result.push(...children);
      }
      return reorderWithPinnedDates(result);
    });
  }, [stableSetSteps]);
  const restoreSteps = useCallback(
  // Typed against the shared CarePlanStep so the patient and template hooks
  // expose the same CarePlanActions.restoreSteps signature. Patient trees
  // never contain TemplateSteps at runtime — the cast is safe.
  (snapshot: CarePlanStep[]) => {
    stableSetSteps(snapshot as PatientCarePlanStep[]);
  }, [stableSetSteps]);

  /**
   * Unified DnD move that handles same-container reorder and cross-container
   * moves between root and branches. After mutating the tree, re-derives
   * stepNumber + scheduledDate for each container that was touched.
   */
  const moveStepTo = useCallback((params: DragOverMoveParams) => {
    // `removed` is populated inside the steps updater below. Both updaters
    // are queued in the same batch, but the steps updater can run AFTER the
    // synchronous code returns — so we cannot read `removed` here directly.
    // Instead we queue the cycles updater unconditionally and read `removed`
    // from the closure when it actually runs (after the steps updater).
    let removed: string[] = [];
    stableSetSteps(prev => {
      const r = applyMoveAndReconcile(prev, params);
      removed = r.removedCycleIds;
      return r.steps;
    });
    if (setCyclesRef) {
      setCyclesRef(prev => removed.length > 0 ? removeCyclesAndReparent(prev, removed) : prev);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [stableSetSteps, cyclesRef, setCyclesRef]);
  const applyDragOverMove = useCallback((info: DragOverMoveInfo) => {
    let removed: string[] = [];
    stableSetSteps(prev => {
      const move = resolveDragOverMove(prev, info);
      if (!move) return prev;
      const r = applyMoveAndReconcile(prev, move);
      removed = r.removedCycleIds;
      return r.steps;
    });
    if (setCyclesRef) {
      setCyclesRef(prev => removed.length > 0 ? removeCyclesAndReparent(prev, removed) : prev);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [stableSetSteps, cyclesRef, setCyclesRef]);
  const addBranch = useCallback((decisionStepId: string) => {
    stableSetSteps(prev => {
      const target = findStep(prev, decisionStepId);
      if (!target) return prev;
      const branches = target.branches ?? [];
      const nextPosition = branches.length;
      const label = String.fromCharCode(65 + nextPosition);
      const newBranch: CarePlanBranch = {
        id: createId("cpb"),
        decisionStepId,
        position: nextPosition,
        name: `Option ${label}`,
        tagIds: [],
        steps: []
      };
      return updateStepInTree(prev, decisionStepId, {
        branches: [...branches, newBranch]
      });
    });
  }, [stableSetSteps]);
  const removeBranch = useCallback((decisionStepId: string, branchId: string) => {
    stableSetSteps(prev => {
      const target = findStep(prev, decisionStepId);
      if (!target) return prev;
      const branches = (target.branches ?? []).filter(b => b.id !== branchId).map((b, i) => ({
        ...b,
        position: i
      }));
      return updateStepInTree(prev, decisionStepId, {
        branches
      });
    });
  }, [stableSetSteps]);
  const addBranchStep = useCallback((branchId: string, type?: StepType) => {
    const newStep = createDefaultStep(0, type);
    stableSetSteps(prev => updateBranchInTree(prev, branchId, (branch, decisionStep) => {
      const lastTimelineStep = [...branch.steps].reverse().find(s => !isLinkedMessage(s));
      let scheduledDate: string;
      let minutesAfterPrevious = DEFAULT_OFFSET_MINUTES;
      if (lastTimelineStep && isPatientStep(lastTimelineStep)) {
        const lastDate = new Date(lastTimelineStep.scheduledDate);
        scheduledDate = new Date(lastDate.getTime() + DEFAULT_OFFSET_MINUTES * 60 * 1000).toISOString();
      } else if (isPatientStep(decisionStep)) {
        scheduledDate = new Date(new Date(decisionStep.scheduledDate).getTime() + DEFAULT_OFFSET_MINUTES * 60 * 1000).toISOString();
      } else {
        scheduledDate = (startDate ?? new Date()).toISOString();
        minutesAfterPrevious = 0;
      }
      const decisionDate = isPatientStep(decisionStep) ? new Date(decisionStep.scheduledDate) : startDate ?? new Date();
      return {
        ...branch,
        steps: recalculateBranchPositions([...branch.steps, {
          ...newStep,
          stepNumber: branch.steps.length + 1,
          scheduledDate,
          minutesAfterPrevious,
          branchId: branch.id
        }], decisionDate)
      };
    }));
    setSelectedStepId(newStep.id);
  }, [stableSetSteps, startDate]);
  const removeBranchStep = useCallback((branchId: string, stepId: string) => {
    stableSetSteps(prev => updateBranchInTree(prev, branchId, (branch, decisionStep) => {
      const stepToRemove = branch.steps.find(s => s.id === stepId);
      if (stepToRemove && isPatientStep(stepToRemove) && (stepToRemove.status === CarePlanStepStatus.COMPLETED || stepToRemove.eventId || stepToRemove.taskId)) {
        return branch;
      }
      const decisionDate = isPatientStep(decisionStep) ? new Date(decisionStep.scheduledDate) : new Date();
      return {
        ...branch,
        steps: recalculateBranchPositions(branch.steps.filter(s => s.id !== stepId && s.parentStepId !== stepId), decisionDate)
      };
    }));
    setSelectedStepId(curr => curr === stepId ? null : curr);
    setExpandedStepId(curr => curr === stepId ? null : curr);
  }, [stableSetSteps]);
  const moveBranchStep = useCallback((branchId: string, fromIndex: number, toIndex: number) => {
    stableSetSteps(prev => updateBranchInTree(prev, branchId, (branch, decisionStep) => {
      const timeline = branch.steps.filter(s => !isLinkedMessage(s));
      if (fromIndex < 0 || fromIndex >= timeline.length || toIndex < 0 || toIndex >= timeline.length || fromIndex === toIndex) {
        return branch;
      }
      const reordered = [...timeline];
      const [removed] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, removed);
      const childrenMap = new Map<string, CarePlanStep[]>();
      for (const s of branch.steps) {
        if (isLinkedMessage(s)) {
          const children = childrenMap.get(s.parentStepId) ?? [];
          children.push(s);
          childrenMap.set(s.parentStepId, children);
        }
      }
      const result: CarePlanStep[] = [];
      for (const step of reordered) {
        result.push(step);
        const children = childrenMap.get(step.id);
        if (children) result.push(...children);
      }
      const decisionDate = isPatientStep(decisionStep) ? new Date(decisionStep.scheduledDate) : new Date();
      return {
        ...branch,
        steps: recalculateBranchPositions(result, decisionDate)
      };
    }));
  }, [stableSetSteps]);
  const updateBranchStep = useCallback((branchId: string, stepId: string, updates: Partial<PatientCarePlanStep>) => {
    stableSetSteps(prev => updateBranchInTree(prev, branchId, (branch, decisionStep) => {
      const updated = updateStepInTree(branch.steps, stepId, updates);
      const decisionDate = isPatientStep(decisionStep) ? new Date(decisionStep.scheduledDate) : new Date();
      if (!("scheduledDate" in updates)) {
        return {
          ...branch,
          steps: recalculateBranchPositions(updated, decisionDate)
        };
      }
      const timeline = updated.filter(s => !isLinkedMessage(s));
      timeline.sort((a, b) => {
        const aDate = isPatientStep(a) ? a.scheduledDate : "";
        const bDate = isPatientStep(b) ? b.scheduledDate : "";
        const dateDiff = new Date(aDate).getTime() - new Date(bDate).getTime();
        if (dateDiff !== 0) return dateDiff;
        return (a.cyclePass ?? 0) - (b.cyclePass ?? 0);
      });
      const childrenMap = new Map<string, CarePlanStep[]>();
      for (const s of updated) {
        if (isLinkedMessage(s)) {
          const children = childrenMap.get(s.parentStepId) ?? [];
          children.push(s);
          childrenMap.set(s.parentStepId, children);
        }
      }
      const result: CarePlanStep[] = [];
      for (const step of timeline) {
        result.push(step);
        const children = childrenMap.get(step.id);
        if (children) result.push(...children);
      }
      return {
        ...branch,
        steps: recalculateBranchPositions(result, decisionDate)
      };
    }));
  }, [stableSetSteps]);
  const duplicateBranchStep = useCallback((branchId: string, step: CarePlanStep, atTimelineIndex: number) => {
    if (!isPatientStep(step)) return;
    const sourceDate = new Date(step.scheduledDate).getTime();
    const newDate = new Date(sourceDate + (step.minutesAfterPrevious ?? DEFAULT_OFFSET_MINUTES) * 60 * 1000).toISOString();

    // DFS: find all cycles self-contained within this step
    const selfContained = options?.cycles?.length ? findSelfContainedCycles(step, steps, options.cycles) : [];
    const cycleIdMap = new Map<string, string>();
    const pendingLocations: Array<{
      cycle: CarePlanCycleDetail;
      loc: ReturnType<typeof locateCycleInStep>;
    }> = [];
    for (const cycle of selfContained) {
      const newId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      cycleIdMap.set(cycle.id, newId);
      pendingLocations.push({
        cycle: {
          ...cycle,
          id: newId
        },
        loc: locateCycleInStep(step, cycle.id)
      });
    }
    // Remap parentCycleId so child cycles point to the new parent
    for (const entry of pendingLocations) {
      if (entry.cycle.parentCycleId && cycleIdMap.has(entry.cycle.parentCycleId)) {
        entry.cycle = {
          ...entry.cycle,
          parentCycleId: cycleIdMap.get(entry.cycle.parentCycleId)!
        };
      }
    }
    let newStep = deepCloneStep(step);
    newStep.scheduledDate = newDate;
    if (cycleIdMap.size > 0) {
      newStep = remapCycleIds(newStep, cycleIdMap);
    }
    const branchIdMapInner = buildBranchIdMap(step, newStep);
    const duplicatedCycles: DuplicatedCycleInfo[] = [];
    for (const {
      cycle,
      loc
    } of pendingLocations) {
      if (!loc) continue;
      if (loc.branchId === null) {
        duplicatedCycles.push({
          cycle,
          fromStepId: newStep.id,
          toStepId: newStep.id,
          branchId
        });
      } else {
        const newBranchId = branchIdMapInner.get(loc.branchId) ?? loc.branchId;
        const branch = newStep.branches?.find(b => b.id === newBranchId);
        const timeline = branch ? branch.steps.filter(s => !isLinkedMessage(s)) : [];
        const fromId = timeline[loc.fromIndex]?.id;
        const toId = timeline[loc.toIndex]?.id;
        if (fromId && toId) {
          duplicatedCycles.push({
            cycle,
            fromStepId: fromId,
            toStepId: toId,
            branchId: newBranchId
          });
        }
      }
    }
    stableSetSteps(prev => updateBranchInTree(prev, branchId, (branch, decisionStep) => {
      const children = branch.steps.filter(s => isLinkedMessage(s) && s.parentStepId === step.id && isPatientStep(s));
      const newChildMessages: CarePlanStep[] = children.map(c => {
        const childDate = isPatientStep(c) ? c.scheduledDate : newDate;
        const offsetMs = new Date(childDate).getTime() - sourceDate;
        return {
          ...c,
          id: createId("cps"),
          parentStepId: newStep.id,
          scheduledDate: new Date(new Date(newDate).getTime() + offsetMs).toISOString(),
          status: CarePlanStepStatus.PENDING,
          eventId: undefined
        };
      });
      const insertAt = findStepInsertIndex(branch.steps, atTimelineIndex);
      const toInsert: CarePlanStep[] = [newStep, ...newChildMessages];
      const next = [...branch.steps.slice(0, insertAt), ...toInsert, ...branch.steps.slice(insertAt)];
      const decisionDate = isPatientStep(decisionStep) ? new Date(decisionStep.scheduledDate) : new Date();
      return {
        ...branch,
        steps: recalculateBranchPositions(next, decisionDate)
      };
    }));
    if (duplicatedCycles.length > 0) {
      options?.onCyclesDuplicated?.(duplicatedCycles);
    }
    setSelectedStepId(newStep.id);
  }, [stableSetSteps, steps, options?.cycles, options?.onCyclesDuplicated]);
  const updateBranch = useCallback((branchId: string, updates: Partial<Pick<CarePlanBranch, "name" | "tagIds">>) => {
    stableSetSteps(prev => updateBranchInTree(prev, branchId, branch => ({
      ...branch,
      ...updates
    })));
  }, [stableSetSteps]);
  return {
    steps: enrichedSteps,
    selectedStepId,
    selectedStep,
    expandedStepId,
    addStep,
    duplicateStep,
    updateStep,
    removeStep,
    selectStep,
    moveStep,
    moveStepTo,
    applyDragOverMove,
    restoreSteps,
    toggleStepExpanded,
    addBranch,
    removeBranch,
    updateBranch,
    addBranchStep,
    removeBranchStep,
    moveBranchStep,
    updateBranchStep,
    duplicateBranchStep
  };
}